import { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser';
import { useRuleMetadata } from '../../metadata/RuleMetadataProvider';
import { metadataRefOptions } from './valueDescriptor';

/**
 * Turns an `entityRefType` from the metadata endpoint into a real dropdown of named things, so a
 * rule author never types a uuid or a code by hand.
 *
 * **Two value conventions**, mirroring `facts/ExpeditionFacts.kt`:
 *
 * - Lookup tables with a stable `code` — truck type, cargo type, loading types, special
 *   requirements — store the **code**, because `requirements contains "ADR"` is a rule a human can
 *   read.
 * - Real entity references — carriers, users, folders, labels — store the **id**, because they are
 *   per-tenant rows with no stable external name.
 *
 * Either way the author picks a name and the option's `value` is what goes into the AST.
 */
export interface RefOption {
  label: string;
  value: string;
}

type Api = React.ContextType<typeof ApiContext>;

interface LoadContext {
  api: Api;
  /** The company whose rules are being edited: the admin's chosen one, or the user's own. */
  companyId: string;
}

interface RefSource {
  /**
   * True when the endpoint takes the company implicitly from the logged-in user, so it cannot
   * answer for another one. Those sources are unavailable in the admin section, where the target
   * company is a different company than the admin's own.
   */
  ownCompanyOnly: boolean;
  /**
   * Also offered by the text editor's autocomplete after an operator. Only for values that are
   * valid bare words in the DSL and readable as typed (`in [RUS, BLR]`): a uuid in a popup helps
   * nobody, since the popup filters on what gets inserted, not on the name beside it.
   */
  offerInText?: boolean;
  load: (context: LoadContext, onLoaded: (options: RefOption[]) => void, onError: () => void) => void;
}

const byName = (left: RefOption, right: RefOption) => left.label.localeCompare(right.label);

const SOURCES: Record<string, RefSource> = {
  CARRIER: {
    ownCompanyOnly: true,
    load: ({ api }, onLoaded, onError) =>
      api.cuLovCarriers('', data => onLoaded(data.map(item => ({ value: item.id, label: item.name })).sort(byName)), null, { onError }),
  },
  CARRIER_GROUP: {
    ownCompanyOnly: true,
    load: ({ api }, onLoaded, onError) =>
      api.cuListCarrierGroups({ pageNumber: 1, pageSize: 200 }, '', data => onLoaded((data.data ?? []).map(item => ({ value: item.carrierGroupId, label: item.name })).sort(byName)), null, {
        onError,
      }),
  },
  USER: {
    ownCompanyOnly: false,
    load: ({ api, companyId }, onLoaded, onError) =>
      api.lovUsersForCompany('', companyId, data => onLoaded(data.map(item => ({ value: item.id, label: item.name })).sort(byName)), null, { onError }),
  },
  COMPANY_FOLDER: {
    ownCompanyOnly: true,
    load: ({ api }, onLoaded, onError) =>
      api.lovCompanyFolders('', 'EXPEDITION', data => onLoaded(data.map(item => ({ value: item.id, label: item.name })).sort(byName)), null, { onError }),
  },
  COMPANY_LABEL: {
    ownCompanyOnly: true,
    load: ({ api }, onLoaded, onError) =>
      api.getAllLabelsForCompanyEntityType('EXPEDITION', '', data => onLoaded(data.map(item => ({ value: item.labelId, label: item.labelValue })).sort(byName)), null, { onError }),
  },
  TRUCK_TYPE: {
    ownCompanyOnly: false,
    load: ({ api }, onLoaded, onError) => api.lovTruckTypes(data => onLoaded(data.map(item => ({ value: item.code, label: item.name })).sort(byName)), null, { onError }),
  },
  CARGO_TYPE: {
    ownCompanyOnly: false,
    load: ({ api }, onLoaded, onError) => api.lovCargoType(data => onLoaded(data.map(item => ({ value: item.code, label: item.name })).sort(byName)), null, { onError }),
  },
  LOADING_TYPE: {
    ownCompanyOnly: false,
    load: ({ api }, onLoaded, onError) => api.lovLoadingTypes(data => onLoaded(data.map(item => ({ value: item.code, label: item.name })).sort(byName)), null, { onError }),
  },
  // A `company_locations` id. The endpoint filters by what the *logged-in* company may see, so in
  // the admin section it would list only the target company's public locations — hence own-company
  // only. Partner locations kept in the company's list are company locations too, so they stay in
  // (`filterPartnerLocations: false`). Address-book stops are a different table and never match.
  COMPANY_LOCATION: {
    ownCompanyOnly: true,
    load: ({ api, companyId }, onLoaded, onError) =>
      api.lovGetAllLocationsForCompanyId(
        '',
        false,
        false,
        null,
        'EXPEDITION',
        companyId,
        data => onLoaded(data.map(item => ({ value: item.id, label: item.name })).sort(byName)),
        null,
        { onError },
      ),
  },
  // The value is the ISO3 code (`CZE`), which is what locations store and what rules compare.
  COUNTRY: {
    ownCompanyOnly: false,
    offerInText: true,
    load: ({ api }, onLoaded, onError) => api.lovCountry(data => onLoaded(data.map(item => ({ value: item.code, label: item.name })).sort(byName)), null, { onError }),
  },
  // Handling-unit codes (`EP`, `IBC`) for a price tariff's `by`: the company's own cargo templates,
  // or the global list when it has none. Codes are bare words, so the text editor offers them too.
  HANDLING_UNIT: {
    ownCompanyOnly: false,
    offerInText: true,
    load: ({ api, companyId }, onLoaded, onError) =>
      api.lovCargoItemTypeForCompany(companyId, data => onLoaded(data.map(item => ({ value: item.code, label: item.name })).sort(byName)), null, { onError }),
  },
  SPECIAL_REQUIREMENT: {
    ownCompanyOnly: false,
    load: ({ api }, onLoaded, onError) => api.lovSpecialRequirements(data => onLoaded(data.map(item => ({ value: item.code, label: item.name })).sort(byName)), null, { onError }),
  },
};

/**
 * Loaded lists, shared by every widget on the page. A rule can easily hold half a dozen
 * conditions over the same fact, and each one would otherwise fetch the catalogue again.
 * Module-level on purpose: the lifetime is the page, and a reload is what refreshes it.
 */
const cache = new Map<string, RefOption[]>();
const inFlight = new Map<string, Promise<RefOption[]>>();
/** Mounted pickers per list, told when a list is reloaded — so a label created in one picker shows up in the others. */
const listeners = new Map<string, Set<(options: RefOption[]) => void>>();

/** Whether a source can answer for the target company — see `ownCompanyOnly`. */
const isSupported = (source: RefSource | undefined, companyId: string | undefined) => Boolean(source) && !(source?.ownCompanyOnly && Boolean(companyId));

const cacheKeyOf = (entityRefType: string, targetCompanyId: string | undefined) => `${entityRefType}:${targetCompanyId ?? ''}`;

/** One list, from the cache or the API. Concurrent requests for the same list share one call. */
const fetchRefOptions = (source: RefSource, cacheKey: string, context: LoadContext): Promise<RefOption[]> => {
  const cached = cache.get(cacheKey);
  if (cached) return Promise.resolve(cached);
  const pending = inFlight.get(cacheKey);
  if (pending) return pending;

  const request = new Promise<RefOption[]>((resolve, reject) =>
    source.load(
      context,
      loaded => {
        cache.set(cacheKey, loaded);
        resolve(loaded);
      },
      () => reject(new Error(`Could not load ${cacheKey}`)),
    ),
  ).finally(() => inFlight.delete(cacheKey));
  inFlight.set(cacheKey, request);
  return request;
};

export interface EntityRefOptionsResult {
  options: RefOption[];
  /** False when there is no picker for this type — the caller falls back to a plain input. */
  supported: boolean;
  loading: boolean;
  failed: boolean;
  /** Drops the cached list and loads it again, for every picker showing it — after the author created an entry. */
  reload: () => Promise<RefOption[]>;
}

export const useEntityRefOptions = (entityRefType?: string, companyId?: string): EntityRefOptionsResult => {
  const api = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const { metadata } = useRuleMetadata();
  // Lists the metadata already carries (country groups) need no fetch and no cache.
  const fromMetadata = metadataRefOptions(metadata, entityRefType);

  const source = entityRefType ? SOURCES[entityRefType] : undefined;
  const targetCompanyId = companyId ?? loggedUser?.companyId;
  // In the admin section the target company is not the logged-in user's, so an endpoint that
  // infers the company would quietly answer for the wrong one. Better no picker than wrong names.
  const supported = isSupported(source, companyId);
  const cacheKey = cacheKeyOf(entityRefType, targetCompanyId);

  const [options, setOptions] = useState<RefOption[]>(() => cache.get(cacheKey) ?? []);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!supported) return undefined;
    const subscribers = listeners.get(cacheKey) ?? new Set();
    listeners.set(cacheKey, subscribers);
    subscribers.add(setOptions);
    return () => {
      subscribers.delete(setOptions);
    };
  }, [cacheKey, supported]);

  const reload = () => {
    cache.delete(cacheKey);
    return fetchRefOptions(source, cacheKey, { api, companyId: targetCompanyId }).then(loaded => {
      listeners.get(cacheKey)?.forEach(notify => notify(loaded));
      return loaded;
    });
  };

  useEffect(() => {
    if (!supported || !targetCompanyId) return undefined;

    const cached = cache.get(cacheKey);
    if (cached) {
      setOptions(cached);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setFailed(false);
    fetchRefOptions(source, cacheKey, { api, companyId: targetCompanyId }).then(
      loaded => {
        if (!active) return;
        setOptions(loaded);
        setLoading(false);
      },
      () => {
        if (!active) return;
        setLoading(false);
        setFailed(true);
      },
    );

    return () => {
      active = false;
    };
  }, [api, cacheKey, source, supported, targetCompanyId]);

  if (fromMetadata) return { options: fromMetadata, supported: true, loading: false, failed: false, reload: () => Promise.resolve(fromMetadata) };
  return { options, supported, loading, failed, reload };
};

/** Loads the values the text editor may offer for an `entityRefType`, or undefined when it offers none. */
export type TextRefOptionsLoader = (entityRefType: string) => Promise<RefOption[]> | undefined;

/**
 * The text editor's access to the same lists and cache as the builder's pickers, limited to the
 * sources marked `offerInText`. Rebuilt every render (ApiContext is), so hold it in a ref.
 */
export const useTextRefOptionsLoader = (companyId?: string): TextRefOptionsLoader => {
  const api = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const targetCompanyId = companyId ?? loggedUser?.companyId;

  return entityRefType => {
    const source = SOURCES[entityRefType];
    if (!source?.offerInText || !isSupported(source, companyId) || !targetCompanyId) return undefined;
    return fetchRefOptions(source, cacheKeyOf(entityRefType, targetCompanyId), { api, companyId: targetCompanyId });
  };
};

/** Re-exported for existing imports; the map now lives beside the param descriptor. */
export { ACTION_PARAM_REF_TYPES } from './valueDescriptor';
